import { Button, Card, Form, Input, Select } from 'antd';
import { observer } from 'mobx-react';
import { RolePermissionType } from 'permission/role/type/roleType';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CompanyInterface } from 'type/companyType';
import * as Component from '../../../../shared/components';
import { getUserEditHandlers } from '../handlers/UserEditHandlers';
import { userService } from '../service/userService';

const UserEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [companyList, setCompanyList] = useState<CompanyInterface[]>([]);
  const [roleList, setRoleList] = useState<RolePermissionType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { onFinish } = getUserEditHandlers({ id: Number(id), form, navigate });

  useEffect(() => {
    Promise.all([
      userService.fetchUserWithRole(Number(id)),
      userService.fetchUserSettingData(),
    ]).then(([user, { roleList, companyList }]) => {
      setRoleList(roleList);
      setCompanyList(companyList);
      form.setFieldsValue(user);
      setIsLoading(false);
    });
  }, [id]);

  return (
    <Component.BasePageComponent>
      {isLoading ? (
        <Component.LoadingSpinnerComponent />
      ) : (
        <div className="flex justify-center items-center min-h-screen bg-gray-100 p-6">
          <Card className="w-full max-w-lg p-6 shadow-lg rounded-2xl bg-white">
            <h3
              className="text-center mb-4 text-xl font-semibold"
              style={{ color: '#000', marginBottom: '30px' }}
            >
              Edit User
            </h3>
            <Form form={form} onFinish={onFinish} layout="vertical">
              <Form.Item name={['platform', 'title']} label="Platform" rules={[{ required: true }]}>
                <Input className="rounded-lg" readOnly />
              </Form.Item>
              <Form.Item
                name="username"
                label="ID"
                rules={[{ required: true, message: 'Please enter a username' }]}
              >
                <Input className="rounded-lg" />
              </Form.Item>

              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter an email' },
                  { type: 'email', message: 'Please enter a valid email address' },
                ]}
              >
                <Input className="rounded-lg" />
              </Form.Item>

              <div className="grid grid-cols-2 gap-4">
                <Form.Item name="first_name" label="First Name" rules={[{ required: true }]}>
                  <Input className="rounded-lg" />
                </Form.Item>
                <Form.Item name="last_name" label="Last Name" rules={[{ required: true }]}>
                  <Input className="rounded-lg" />
                </Form.Item>
              </div>
              <Form.Item name={['company', 'id']} label="Organization">
                <Select className="rounded-lg">
                  {companyList.map(company => (
                    <Select.Option value={company.id} key={company.id}>
                      {company.title}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="roles"
                label="Role"
                getValueProps={(selectedRoles: RolePermissionType[]) => ({
                  value: selectedRoles?.map(role => role.id) || [],
                })}
                getValueFromEvent={(value: number[]) =>
                  roleList.filter(role => value.includes(role.id))
                }
              >
                <Select mode="multiple">
                  {roleList.map(role => (
                    <Select.Option value={role.id} key={role.id} label={role.name}>
                      {role.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Component.LgChannelVietnamField scope="user" />
              <Component.LgChannelVietnamWarning />

              <Form.Item>
                <Button type="primary" htmlType="submit" className="w-full rounded-lg text-lg">
                  Edit
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </div>
      )}
    </Component.BasePageComponent>
  );
};

export default observer(UserEdit);
